import React, {
  useEffect,
  useRef,
  useState,
  useContext,
  useReducer,
  useCallback,
} from "react";
import { useLocation, useParams, Redirect } from "react-router-dom";
import { MEETING_QUERY_PARAMS, APP_EVENT_ID } from "../utils/consts";
import MeetingNotes from "./MeetingNotes";
import MeetingRating from "./MeetingRating";
import CompanyHeader from "./CompanyHeader";
import {
  fetchMeetingDetails,
  saveMeetingRating,
  addAttendeeToMeeting,
} from "../api/xchange-api";
import { UserContext, USER_ROLES, USER_COMPANY_TYPE } from "./UserProvider";
import useNotifyOnMeetingLeave from "../hooks/useNotifyOnMeetingLeave";
import SideTabs from "./SideTabs";
import MeetingFollowUp from "./MeetingFollowUp";
import { MeetingContext } from "./MeetingProvider";
import ErrorPage from "./ErrorPage";
import LeaveMeetingButton from "./LeaveMeetingButton";
import DefaultMeetingEventLogo from "./DefaultMeetingEventLogo";
import MeetingControls from "./MeetingControls";

const MEETING_TOOL_ACTIONS = {
  INITIALIZE: "INITIALIZE",
  SET_RATING: "SET_RATING",
  SET_HOST_NOTES: "SET_HOST_NOTES",
  SET_FOLLOWUP: "SET_FOLLOWUP",
  SAVING: "SAVING",
  SAVED: "SAVED",
};

function meetingToolsReducer(state, { type, value }) {
  switch (type) {
    case MEETING_TOOL_ACTIONS.INITIALIZE:
      // set initial state
      return {
        initial: true,
        ...value,
      };
    case MEETING_TOOL_ACTIONS.SET_RATING:
      return {
        ...state,
        Rating: value,
        initial: false,
      };

    case MEETING_TOOL_ACTIONS.SET_HOST_NOTES:
      return {
        ...state,
        Notes: value,
        initial: false,
      };

    case MEETING_TOOL_ACTIONS.SET_FOLLOWUP:
      return {
        ...state,
        FollowUp: value,
        initial: false,
      };

    default:
      return state;
  }
}

// State used by the meetingToolsReducer function to manage the
// state of the meeting tools (rating, follow-up and notes)
const initialMeetingToolsState = {
  Rating: null,
  Notes: null,
  FollowUp: null,
};

const GeneralMeetingRoom = ({
  id,
  sidebarCollapsed,
  companyProfile,
  meetingId,
  chatLoaded,
}) => {
  const { userProfile, role, fullName } = useContext(UserContext);
  const iframeRef = useRef(null);
  const location = useLocation();
  const { companyId } = useParams();
  const meetingDetailsParam =
    location.state && location.state.meetingDetails
      ? location.state.meetingDetails
      : null;

  const [meetingDetails, setMeetingDetails] = useState(null);
  const [meetingLink, setMeetingLink] = useState(null);
  const [isInvalidMeeting, setIsInvalidMeeting] = useState(false);
  const [isSavingMeetingTools, setIsSavingMeetingTools] = useState(false);
  const [userCompanyType, setUserCompanyType] = useState();
  const [userIsInvitee, setUserIsInvitee] = useState(null);

  const [meetingToolsState, dispatch] = useReducer(
    meetingToolsReducer,
    initialMeetingToolsState
  );

  const onMeetingNotesSubmit = useCallback((notes) => {
    dispatch({ type: MEETING_TOOL_ACTIONS.SET_HOST_NOTES, value: notes });
  }, []);

  const onMeetingFollowUpChanged = useCallback((followUpRequested) => {
    dispatch({
      type: MEETING_TOOL_ACTIONS.SET_FOLLOWUP,
      value: followUpRequested,
    });
  }, []);

  const onMeetingRatingChanged = useCallback((rating) => {
    dispatch({
      type: MEETING_TOOL_ACTIONS.SET_RATING,
      value: +rating,
    });
  }, []);

  // Hook to notify when guest has left meeting
  useNotifyOnMeetingLeave({
    companyId,
    meetingId,
    userIsInvitee,
    userProfile,
    role,
  });

  useEffect(() => {
    let unmounted = false;

    const loadMeetingDetails = async () => {
      let _meetingDetails = null;
      if (meetingDetailsParam !== null) {
        _meetingDetails = meetingDetailsParam;
      } else {
        try {
          _meetingDetails = await fetchMeetingDetails(meetingId);
        } catch (error) {
          if (unmounted) {
            return;
          }
          setIsInvalidMeeting(true);
        }
      }

      if (_meetingDetails === null) {
        setIsInvalidMeeting(true);
      }

      if (unmounted || !_meetingDetails) {
        return;
      }

      setMeetingDetails(_meetingDetails);
      if (_meetingDetails.VirtualMeeting) {
        setMeetingLink(
          role === USER_ROLES.HOST
            ? `${_meetingDetails.VirtualMeeting.HostMeetingLink}&${MEETING_QUERY_PARAMS}&displayName=${fullName}`
            : `${_meetingDetails.VirtualMeeting.MeetingLink}?${MEETING_QUERY_PARAMS}&displayName=${fullName}`
        );

        if (
          _meetingDetails.VirtualMeeting.CompanyMeeting &&
          _meetingDetails.VirtualMeeting.CompanyMeeting.Ratings &&
          _meetingDetails.VirtualMeeting.CompanyMeeting.Ratings.length > 0
        ) {
          const userRating = _meetingDetails.VirtualMeeting.CompanyMeeting.Ratings.find(
            ({ AttendeeID }) => {
              return AttendeeID === userProfile.AttendeeID;
            }
          );

          if (userRating) {
            // Initialize the meeting tools state
            dispatch({
              type: MEETING_TOOL_ACTIONS.INITIALIZE,
              value: {
                Rating: userRating.Rating,
                Notes: userRating.Notes,
                FollowUp: userRating.FollowUp,
              },
            });
          }
        }
      }
    };

    if (
      userProfile &&
      companyProfile &&
      role &&
      role !== USER_ROLES.RECEPTIONIST
    ) {
      loadMeetingDetails();
    }

    return () => (unmounted = true);
  }, [
    userProfile,
    companyProfile,
    meetingId,
    meetingDetailsParam,
    role,
    fullName,
  ]);

  // Send Meeting Tool values (rating, followup, notes) to the API
  const saveMeetingTools = useCallback(async () => {
    try {
      setIsSavingMeetingTools(true);
      await saveMeetingRating(meetingDetails.EventID, {
        CompanyProfileID: +companyId,
        AttendeeID: +userProfile.AttendeeID,
        EventID: meetingDetails.EventID,
        Rating: meetingToolsState.Rating,
        Notes: meetingToolsState.Notes,
        FollowUp: meetingToolsState.FollowUp,
      });
    } catch (e) {
      console.error("Error saving meeting tools: ", e);
    } finally {
      setIsSavingMeetingTools(false);
    }
  }, [meetingToolsState, meetingDetails, companyId, userProfile]);

  // Save Meeting Tool values (rating, followup, notes) whenever the
  // user has updated them
  useEffect(() => {
    // if this is the initial state, don't attempt to re-save it since
    // we've just initialized it from the server
    if (
      meetingToolsState === initialMeetingToolsState ||
      meetingToolsState.initial
    ) {
      return;
    }

    saveMeetingTools();
  }, [meetingToolsState, saveMeetingTools]);

  useEffect(() => {
    const addUserToMeeting = async () => {
      try {
        const result = await addAttendeeToMeeting(
          meetingId,
          userProfile.AttendeeID,
          APP_EVENT_ID
        );

        // if there was an error adding the attendee to the meeting, redirect them
        // to reception as they are not an invitee.
        setUserIsInvitee(!result.error);
      } catch (e) {
        console.error("Error adding attending to meeting: ", e);
      }
    };

    if (userProfile && meetingDetails) {
      // Check if user is an invitee to the meeting; if not,
      // we can redirect them back to reception; otherwise,
      // notify the backend that the user has arrived to the
      // meeting.
      const userIsInviteeInMeeting = meetingDetails.PersonalEventsParticipants.some(
        (invitee) => invitee.AttendeeID === userProfile.AttendeeID
      );

      if (userIsInviteeInMeeting || role === USER_ROLES.ADMIN) {
        addUserToMeeting();
        setUserIsInvitee(true);
      } else {
        setUserIsInvitee(false);
      }
    }
  }, [userProfile, meetingDetails, meetingId, companyId, role]);

  // Update user company type based on the user's profile
  useEffect(() => {
    if (userProfile && userProfile.ExhibitorInformation) {
      setUserCompanyType(userProfile.ExhibitorInformation.CompanyTypeGroup);
    }
  }, [userProfile]);

  useEffect(() => {
    if (role === USER_ROLES.HOST || role === USER_ROLES.ADMIN) {
      if (chatLoaded && window.MessagingHub && companyId && meetingId) {
        window.MessagingHub.meetingSuite().set({
          CompanyID: companyId,
          MeetingArea: "meeting",
          MeetingID: meetingId,
          UserRole: role,
        });
      }
    }
  }, [role, companyId, meetingId, chatLoaded]);

  return isInvalidMeeting ? (
    <ErrorPage errorText="Invalid Meeting ID" />
  ) : userIsInvitee === false ? (
    <Redirect to={`/${companyId}/reception`} />
  ) : (
    <React.Fragment>
      <div className="row h-100">
        <div className="col-sm-12 col-md-8 col-xl-9">
          <div className="header text-center text-uppercase my-3">
            <CompanyHeader profile={companyProfile} />
          </div>
          <div
            className={`video-container w-100 my-3 ${
              companyProfile && companyProfile.BannerImage
                ? "has-step-repeat-banner"
                : ""
            }`}
            style={{
              position: "relative",
              backgroundImage: `${
                companyProfile && companyProfile.BannerImage
                  ? `url(${companyProfile.BannerImage})`
                  : "none"
              }`,
            }}
          >
            <div className="embed-responsive embed-responsive-16by9">
              {meetingLink && (
                <>
                  <iframe
                    ref={iframeRef}
                    src={meetingLink}
                    className="w-100 h-100 border-0 embed-responsive-item"
                    title={`Meeting room ${id}`}
                    allow="camera; microphone; fullscreen; speaker; display-capture"
                  />
                  <LeaveMeetingButton userRole={role} companyId={companyId} />

                  {companyProfile && !companyProfile.BannerImage ? (
                    <DefaultMeetingEventLogo />
                  ) : null}
                </>
              )}
            </div>

            {(role === USER_ROLES.HOST || role === USER_ROLES.ADMIN) && (
              <MeetingControls
                companyProfile={companyProfile}
                currentMeetingId={meetingId}
              />
            )}
          </div>

          {(role === USER_ROLES.HOST || role === USER_ROLES.ADMIN) &&
            userCompanyType === USER_COMPANY_TYPE.PAVILIONS && (
              <>
                <div className="my-4 d-flex align-items-center justify-content-center">
                  <MeetingRating
                    initialRating={meetingToolsState.Rating}
                    onRatingChanged={onMeetingRatingChanged}
                    className="mx-3"
                  />
                  <MeetingFollowUp
                    followUpStatus={meetingToolsState.FollowUp}
                    onFollowUpChanged={onMeetingFollowUpChanged}
                    className="mx-3"
                  />
                </div>
                <div className="my-4">
                  <MeetingNotes
                    notes={meetingToolsState.Notes}
                    onSave={onMeetingNotesSubmit}
                    isSaving={isSavingMeetingTools}
                  />
                </div>
              </>
            )}
        </div>

        <div className="col-sm-12 col-md-4 col-xl-3 px-0">
          <MeetingContext.Consumer>
            {({ meetingAttendeesList }) => (
              <SideTabs
                sidebarCollapsed={sidebarCollapsed}
                attendees={
                  meetingAttendeesList.length ? meetingAttendeesList : []
                }
                attendeesTabLabel="Attendees"
                chatTabLabel="Chat"
                showAttendeesTab={true}
                ShowChatTab={false}
              />
            )}
          </MeetingContext.Consumer>
        </div>
      </div>
    </React.Fragment>
  );
};

export default GeneralMeetingRoom;
